/**
 * The words a circular is tagged with, in one place.
 *
 * The feed and the dashboard card show the same bulletin, so they must show the
 * same tags: the status, the bureau, the kind, and the routing template it
 * belongs to. They were drifting - the feed said "State decision" where the card
 * said "Rate decision" - which reads as two different records.
 */

/** The prototype printed these words; the API sends the snake_case enum. */
export const KIND_LABELS: Record<string, string> = {
  bureau_filing: 'Bureau filing',
  state_decision: 'State decision',
  regulatory_filing: 'Regulatory filing',
  operational: 'Operational',
  state_approval: 'State approval',
  bureau_bulletin: 'Bureau bulletin',
  classification_change: 'Classification change',
  assessment: 'Assessment',
  state_bulletin: 'State bulletin',
  reporting_change: 'Reporting change',
  state_order: 'State order',
  reference: 'Reference',
  hearing: 'Hearing',
}

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind
}

/**
 * The dash the API writes where a record states nothing.
 *
 * Two columns cannot hold NULL - a circular's rate change is one - so absence
 * arrives as this character rather than as an empty field.
 */
export const NOT_STATED = '—'

/**
 * A value the record actually states, or null.
 *
 * Use it wherever a card would otherwise print the dash. The dash earns its
 * place in a facts panel, where a row reading "Effective —" says a change has no
 * date set; it earns nothing beside a figure, where it is a heading with no
 * number under it and reads as something failing to load.
 */
export function stated(value: string | null | undefined): string | null {
  const text = (value ?? '').trim()
  return text && text !== NOT_STATED ? text : null
}

/**
 * "1 circular", "20 circulars".
 *
 * One rule for the word, because it is written in three places and a feed
 * showing a single filing read "1 circulars" in two of them.
 */
export function circularCount(count: number): string {
  return `${count} circular${count === 1 ? '' : 's'}`
}

/**
 * What to call the figure on a card, in the words of whoever published it.
 *
 * The feed says so in `CIRCULAR_LABEL`: Texas and Pennsylvania publish loss
 * costs and the other states publish rates, and the record is the only thing
 * that knows which. A curated circular has no feed record behind it and keeps
 * its own `rateWord` ("approved rate change", "aggregate rate change").
 *
 * Null where neither says anything, and then the card prints no line at all. It
 * used to print "rate change vs prior approved" in that case, which no document
 * said and which was wrong for every loss-cost filing it appeared on.
 */
export function figureLabel(circular: {
  circularLabel?: string | null
  rateWord?: string | null
}): string | null {
  const stated = (circular.circularLabel ?? '').trim()
  if (stated) return stated
  const word = (circular.rateWord ?? '').trim()
  return word ? `${word} rate change` : null
}

/**
 * A long value cut where a reader would cut it: between words, with an ellipsis.
 *
 * A state may put a whole document title in `DOCUMENT_TYPE` - California has
 * sent a 127-character one - and the chip that shows it was capped in CSS, so
 * it read "Commissioner's Decision And Order Septemb…". A cut through the
 * middle of a word reads as a broken value rather than a shortened one. This
 * backs up to the last space, and the full string stays in the tooltip.
 *
 * Returns the value unchanged when it already fits, so nothing short is ever
 * marked as cut. A single word longer than `max` has no space to back up to and
 * is cut where it must be - rare, and still better than overflowing the row.
 */
export function clampWords(value: string, max: number): string {
  const text = value.trim()
  if (text.length <= max) return text
  const head = text.slice(0, max - 1).trimEnd()
  const spaced = head.includes(' ') ? head.slice(0, head.lastIndexOf(' ')) : head
  // Only back up when the word boundary leaves most of the room used: a value
  // whose last space sits near the start would lose more than it saves.
  const kept = spaced.length >= Math.floor((max - 1) / 2) ? spaced : head
  return `${kept.replace(/[\s,;:\-–—]+$/, '')}…`
}
